import { useState, useEffect, useCallback, useRef, useContext } from 'react'

import { AppContext } from '@/components/context/AppContext'
import { getFileExtension, trimFileExtension } from '@/utils/files'
import api from '@/utils/api'
import type {
  ProcessType,
  InitialConversionData,
  ConversionData,
  ConversionErrorReason,
  StartConversionResponse,
  CheckProgressResponse
} from '@/types/conversion'

const useConvertFile = (onFinish?: () => void) => {
  const [process, setProcess] = useState<ProcessType>('pending')
  const [conversionId, setConversionId] = useState<string | null>(null)
  const [conversionData, setConversionData] = useState<ConversionData | null>(null)
  const [errorReason, setErrorReason] = useState<ConversionErrorReason | undefined>(undefined)

  const initialConversionData = useRef<InitialConversionData | null>(null)

  const { mscServiceUrl } = useContext(AppContext)

  const resetConversion = useCallback(() => {
    setProcess('pending')
    setConversionId(null)
    setConversionData(null)
    setErrorReason(undefined)
    initialConversionData.current = null
  }, [])

  const resetErrorReason = useCallback(() => {
    if (process === 'failed') {
      setProcess('pending')
      setErrorReason(undefined)
    }
  }, [process])

  const uploadFile = useCallback(async (file: File, targetFormat: string, extraRequestParams: Record<string, unknown> = {}) => {
    if (initialConversionData.current === null) {
      throw new Error('Not initialized the "initialConversionData" object')
    }

    setProcess('uploading')

    const params = {
      path: 'fc',
      command: 'start',
      request: Object.assign({
        srcFormat: initialConversionData.current.sourceFormat,
        dstFormat: targetFormat,
        dstFilename: initialConversionData.current.destinationFilename
      }, extraRequestParams)
    }

    const response = await api.get<StartConversionResponse>('/events', {
      params,
      baseURL: mscServiceUrl
    })

    const conversionId = response.data.result.id
    const uploadUrl = response.data.result.uploadUrl

    await fetch(uploadUrl, {
      method: 'POST',
      body: file,
      mode: 'no-cors',
      headers: {
        'Content-Type': file.type,
      }
    })

    setConversionId(conversionId)
  }, [mscServiceUrl])

  useEffect(() => {
    if (initialConversionData.current && conversionId && process === 'uploading') {
      setProcess(initialConversionData.current.process)

      const params = {
        path: 'fc',
        command: 'status',
        id: JSON.stringify(conversionId)
      }

      const checkProgressInterval = setInterval(async () => {
        try {
          const response = await api.get<CheckProgressResponse>('/events', {
            params,
            baseURL: mscServiceUrl
          })

          if (response.data.result.status === 'working') {
            return
          }

          if (response.data.result.status === 'finished') {
            clearInterval(checkProgressInterval)
            setProcess('ready')
            setConversionData({
              ...initialConversionData.current,
              id: conversionId,
              downloadUrls: response.data.result.downloadUrls,
              zipDownloadUrl: response.data.result.zipDownloadUrl
            })
          }

          if (response.data.result.status === 'error') {
            clearInterval(checkProgressInterval)
            setProcess('failed')
            setErrorReason(response.data.result.error ?? 'unknown')
          }
        } catch (error: any) {
          clearInterval(checkProgressInterval)
          setProcess('failed')
          setErrorReason(error.message)
        }
      }, 2000)
    }
  }, [conversionId, process, mscServiceUrl])

  // handling conversion completion
  useEffect(() => {
    if (process === 'ready' && onFinish) {
      onFinish()
    }
  }, [process, onFinish])

  const convertFile = useCallback(async (file: File, targetFormat: string) => {
    resetConversion()

    const baseFilename = trimFileExtension(file.name)
    initialConversionData.current = {
      process: 'converting',
      baseFilename,
      sourceFormat: getFileExtension(file.type),
      targetFormat,
      destinationFilename: `${baseFilename}.${targetFormat}`
    }

    await uploadFile(file, targetFormat)
  }, [resetConversion, uploadFile])

  const compressFile = useCallback(async (file: File) => {
    resetConversion()

    const baseFilename = trimFileExtension(file.name)
    const targetFormat = ''
    const extraRequestParams = {
      enginePrefs: ['pdftools'],
      jsonPayload: {
        arguments: ['optimize', '--max-dpi=500', '--jpeg-quallity=9']
      }
    }
    initialConversionData.current = {
      process: 'compressing',
      baseFilename,
      sourceFormat: getFileExtension(file.type),
      targetFormat,
      destinationFilename: `${baseFilename}-compressed.pdf`
    }

    await uploadFile(file, targetFormat, extraRequestParams)
  }, [resetConversion, uploadFile])

  return {
    process,
    conversionId,
    conversionData,
    errorReason,
    convertFile,
    compressFile,
    resetErrorReason
  }
}

export default useConvertFile
